import { focusManager, type QueryClient } from "@tanstack/react-query";

type Cutover = { sessionQuery: object; actorId: string; sessionId: string | null; ready: Promise<void> };
const cutovers = new WeakMap<QueryClient, Cutover>();

function affectedResource(key: readonly unknown[], actorId: string) {
  if (key[0] === "trip-list" || key[0] === "trip-action") return key[1] === actorId;
  const url = key[0] === "raw-reader" && typeof key[3] === "string" ? key[3].split("?")[0]! : null;
  if (!url || /^\/v1\/packing-lists\/shared(?:\/|$)/.test(url)) return false;
  return /^\/v1\/(?:trips|packing-lists)(?:\/|$)/.test(url);
}

/** One current-session cutover for resource email and ticket metadata; discard old snapshots. */
export function ensureResourceEmailContract(client: QueryClient, actorId: string): Promise<void> {
  const sessionQuery = client.getQueryCache().find({ queryKey: ["session"], exact: true });
  const session = sessionQuery?.state.data as { user?: { id?: string }; session?: { id?: string } } | null;
  if (sessionQuery?.state.status !== "success" || session?.user?.id !== actorId) return Promise.resolve();
  const sessionId = session.session?.id ?? null;
  const previous = cutovers.get(client);
  if (previous?.sessionQuery === sessionQuery && previous.actorId === actorId && previous.sessionId === sessionId) return previous.ready;
  const cutover: Cutover = { sessionQuery, actorId, sessionId, ready: Promise.resolve() };
  cutovers.set(client, cutover);
  cutover.ready = Promise.resolve().then(async () => {
    const predicate = (query: { queryKey: readonly unknown[] }) => affectedResource(query.queryKey, actorId);
    await client.cancelQueries({ predicate });
    const current = client.getQueryData<{ user?: { id?: string }; session?: { id?: string } } | null>(["session"]);
    if (cutovers.get(client) !== cutover || client.getQueryCache().find({ queryKey: ["session"], exact: true }) !== sessionQuery || sessionQuery.state.status !== "success" || current?.user?.id !== actorId || (current.session?.id ?? null) !== sessionId) {
      if (cutovers.get(client) === cutover) cutovers.delete(client);
      return;
    }
    // Clear whole snapshots, including initialData; no retired-key mapping or merge.
    for (const query of client.getQueryCache().getAll()) {
      if (!predicate(query)) continue;
      if (!query.getObserversCount()) client.removeQueries({ queryKey: query.queryKey, exact: true });
      else query.setState({ data: undefined, dataUpdatedAt: 0, error: null, status: "pending", isInvalidated: true });
    }
    await client.invalidateQueries({ predicate, refetchType: focusManager.isFocused() ? "active" : "none" });
  }).catch(error => {
    if (cutovers.get(client) === cutover) cutovers.delete(client);
    throw error;
  });
  return cutover.ready;
}
